import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Shield, Flag, EyeOff, Trash2, ExternalLink, Users, FileText, CheckCircle2, Activity } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useServerFn } from "@tanstack/react-start";
import { getOnboardingHealth } from "@/lib/onboarding.functions";
import { ErrorMonitoringPanel } from "@/components/error-monitoring-panel";
import { CivicScoreIntegrityPanel } from "@/components/civic-score-integrity-panel";
import { VideoReviewQueue } from "@/components/video-review-queue";
import { ImpactStoriesEditor } from "@/components/impact-stories-editor";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Xamseen — Moderation & Admin" },
      { name: "description", content: "Review reported content and track platform health across Xamseen." },
      { property: "og:title", content: "Moderation & Admin — Xamseen" },
      { property: "og:description", content: "Review reported content and track platform health across Xamseen." },
    ],
  }),
  component: AdminPage,
});

type Report = {
  id: string; target_type: string; target_id: string; reason: string; details: string | null;
  status: string; created_at: string; reporter_id: string;
};

type TargetPreview = { content: string; author: string | null; hidden: boolean; exists: boolean };

type Kpis = {
  users: number; newUsers7d: number; posts: number; posts7d: number;
  resolved: number; openReports: number; comments7d: number; verifiedOfficials: number;
};

function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<"queue" | "video" | "stories" | "analytics" | "errors" | "integrity">("queue");
  const [reports, setReports] = useState<Report[]>([]);
  const [previews, setPreviews] = useState<Record<string, TargetPreview>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);

  const loadQueue = useCallback(async () => {
    const { data } = await supabase.from("reports").select("*").order("created_at", { ascending: false }).limit(100);
    const rows = (data ?? []) as Report[];
    setReports(rows);

    // duplicate-report counts per target (auto-flag signal)
    const tally: Record<string, number> = {};
    rows.forEach((r) => { tally[r.target_id] = (tally[r.target_id] ?? 0) + 1; });
    setCounts(tally);

    const postIds = rows.filter((r) => r.target_type === "post").map((r) => r.target_id);
    const commentIds = rows.filter((r) => r.target_type === "comment").map((r) => r.target_id);
    const map: Record<string, TargetPreview> = {};

    if (postIds.length) {
      const { data: posts } = await supabase
        .from("posts")
        .select("id, content, hidden, author:profiles!posts_author_profile_fkey ( username )")
        .in("id", postIds);
      (posts ?? []).forEach((p: any) => {
        map[p.id] = { content: p.content ?? "", author: p.author?.username ?? null, hidden: !!p.hidden, exists: true };
      });
    }
    if (commentIds.length) {
      const { data: comments } = await supabase
        .from("comments")
        .select("id, content, hidden, author:profiles!comments_author_profile_fkey ( username )")
        .in("id", commentIds);
      (comments ?? []).forEach((c: any) => {
        map[c.id] = { content: c.content ?? "", author: c.author?.username ?? null, hidden: !!c.hidden, exists: true };
      });
    }
    setPreviews(map);
  }, []);

  const loadKpis = useCallback(async () => {
    const since = new Date(Date.now() - 7 * 86400000).toISOString();
    const head = { count: "exact" as const, head: true };
    const [users, newUsers, posts, posts7d, resolved, openReports, comments7d, officials] = await Promise.all([
      supabase.from("profiles").select("id", head),
      supabase.from("profiles").select("id", head).gte("created_at", since),
      supabase.from("posts").select("id", head),
      supabase.from("posts").select("id", head).gte("created_at", since),
      supabase.from("posts").select("id", head).eq("status", "resolved"),
      supabase.from("reports").select("id", head).eq("status", "pending"),
      supabase.from("comments").select("id", head).gte("created_at", since),
      supabase.from("profiles").select("id", head).eq("verified", true),
    ]);
    setKpis({
      users: users.count ?? 0,
      newUsers7d: newUsers.count ?? 0,
      posts: posts.count ?? 0,
      posts7d: posts7d.count ?? 0,
      resolved: resolved.count ?? 0,
      openReports: openReports.count ?? 0,
      comments7d: comments7d.count ?? 0,
      verifiedOfficials: officials.count ?? 0,
    });
  }, []);

  useEffect(() => {
    if (!user) { setAllowed(false); return; }
    supabase.from("user_roles").select("role").eq("user_id", user.id).then(({ data }) => {
      const ok = (data ?? []).some((r: any) => r.role === "admin" || r.role === "moderator");
      setAllowed(ok);
      if (ok) { void loadQueue(); void loadKpis(); }
    });
  }, [user, loadQueue, loadKpis]);

  const resolve = async (id: string, status: "resolved" | "dismissed") => {
    const { error } = await supabase.from("reports")
      .update({ status, resolved_by: user!.id, resolved_at: new Date().toISOString() }).eq("id", id);
    if (error) return toast.error(error.message);
    setReports((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
    toast.success(status === "resolved" ? "Report resolved" : "Report dismissed");
  };

  const toggleHidden = async (r: Report) => {
    const table = r.target_type === "post" ? "posts" : "comments";
    const current = previews[r.target_id]?.hidden ?? false;
    const { error } = await supabase.from(table as any).update({ hidden: !current }).eq("id", r.target_id);
    if (error) return toast.error(error.message);
    setPreviews((p) => ({ ...p, [r.target_id]: { ...(p[r.target_id] as TargetPreview), hidden: !current } }));
    toast.success(!current ? "Content hidden from feeds" : "Content restored");
  };

  const removeContent = async (r: Report) => {
    const table = r.target_type === "post" ? "posts" : "comments";
    const { error } = await supabase.from(table as any).delete().eq("id", r.target_id);
    if (error) return toast.error(error.message);
    setPreviews((p) => ({ ...p, [r.target_id]: { content: "", author: null, hidden: true, exists: false } }));
    await resolve(r.id, "resolved");
    toast.success("Content removed");
  };

  if (allowed === null) return <AppShell><p className="text-muted-foreground">Loading…</p></AppShell>;
  if (!allowed) return (
    <AppShell>
      <Card className="mx-auto max-w-md p-8 text-center">
        <Shield className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <p className="font-medium">Moderator access required</p>
        <p className="text-sm text-muted-foreground">This area is only available to community moderators and admins.</p>
      </Card>
    </AppShell>
  );

  const pending = reports.filter((r) => r.status === "pending");

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Moderation & Admin</h1>
          <p className="text-sm text-muted-foreground">{pending.length} report{pending.length === 1 ? "" : "s"} awaiting review</p>
        </div>

        <div className="flex flex-wrap gap-1 rounded-full bg-muted p-1">
          {([["queue", "Report queue"], ["video", "Video review"], ["stories", "Impact Stories"], ["analytics", "Platform analytics"], ["errors", "Errors"], ["integrity", "Integrity"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={cn("flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                tab === k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>
              {label}
            </button>
          ))}
        </div>

        {tab === "video" ? (
          <VideoReviewQueue />
        ) : tab === "stories" ? (
          <ImpactStoriesEditor />
        ) : tab === "integrity" ? (
          <CivicScoreIntegrityPanel />
        ) : tab === "errors" ? (
          <ErrorMonitoringPanel />
        ) : tab === "analytics" ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Kpi icon={Users} label="Members" value={kpis?.users} sub={kpis ? `+${kpis.newUsers7d} this week` : undefined} />
              <Kpi icon={FileText} label="Reports posted" value={kpis?.posts} sub={kpis ? `+${kpis.posts7d} this week` : undefined} />
              <Kpi icon={CheckCircle2} label="Resolved" value={kpis?.resolved}
                sub={kpis && kpis.posts ? `${Math.round((kpis.resolved / kpis.posts) * 100)}% resolution rate` : undefined} />
              <Kpi icon={Activity} label="Comments (7d)" value={kpis?.comments7d} />
              <Kpi icon={Flag} label="Open flags" value={kpis?.openReports} />
              <Kpi icon={Shield} label="Verified accounts" value={kpis?.verifiedOfficials} />
            </div>
            <OnboardingHealth />
          </div>
        ) : reports.length === 0 ? (

          <Card className="p-12 text-center">
            <Flag className="mx-auto mb-3 h-10 w-10 text-secondary" />
            <p className="font-medium">No open reports</p>
            <p className="text-sm text-muted-foreground">Community content is clean right now.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => {
              const preview = previews[r.target_id];
              const dupes = counts[r.target_id] ?? 1;
              return (
                <Card key={r.id} className={cn("p-4", dupes >= 3 && r.status === "pending" && "border-destructive/50 bg-destructive/5")}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="capitalize">{r.target_type}</Badge>
                        <Badge variant="secondary" className="capitalize">{r.status}</Badge>
                        {dupes >= 3 && <Badge variant="destructive">Auto-flagged · {dupes} reports</Badge>}
                        {preview?.hidden && <Badge variant="outline">Hidden</Badge>}
                        {preview && !preview.exists && <Badge variant="outline">Deleted</Badge>}
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="font-medium capitalize">{r.reason.replace(/_/g, " ")}</p>
                      {r.details && <p className="mt-1 text-sm text-muted-foreground">{r.details}</p>}
                      {preview?.exists && (
                        <div className="mt-2 rounded-lg border border-border bg-muted/40 p-3">
                          <p className="text-xs text-muted-foreground">
                            {preview.author ? `@${preview.author}` : "Unknown author"}
                          </p>
                          <p className="mt-1 line-clamp-4 text-sm">{preview.content}</p>
                        </div>
                      )}
                      {r.target_type === "post" && (
                        <Link to="/posts/$id" params={{ id: r.target_id }}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline">
                          <ExternalLink className="h-3 w-3" />Open post
                        </Link>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-col gap-2">
                      {r.status === "pending" && (
                        <>
                          <Button size="sm" onClick={() => resolve(r.id, "resolved")}>Resolve</Button>
                          <Button size="sm" variant="outline" onClick={() => resolve(r.id, "dismissed")}>Dismiss</Button>
                        </>
                      )}
                      {preview?.exists && r.target_type !== "profile" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => toggleHidden(r)}>
                            <EyeOff className="mr-1.5 h-3.5 w-3.5" />{preview.hidden ? "Unhide" : "Hide"}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => removeContent(r)}>
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Kpi({ icon: Icon, label, value, sub }: {
  icon: React.ComponentType<{ className?: string }>; label: string; value?: number; sub?: string;
}) {
  return (
    <Card className="p-4">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <div className="mt-2 font-display text-2xl font-semibold tabular-nums">
        {value === undefined ? "—" : value.toLocaleString()}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-primary">{sub}</div>}
    </Card>
  );
}

/** Onboarding + account-email health for administrators (no email contents shown). */
function OnboardingHealth() {
  const load = useServerFn(getOnboardingHealth);
  const [data, setData] = useState<Awaited<ReturnType<typeof getOnboardingHealth>> | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    load({ data: undefined as never }).then(setData).catch(() => setError(true));
  }, [load]);

  return (
    <div>
      <h2 className="mb-3 font-display text-base font-semibold">Onboarding health</h2>
      {error ? (
        <Card className="p-6 text-sm text-muted-foreground">Onboarding metrics are unavailable right now.</Card>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Kpi icon={Users} label="New members (7d)" value={data?.newUsers7d} />
          <Kpi icon={CheckCircle2} label="Onboarding completed" value={data?.onboardingCompleted} />
          <Kpi icon={Activity} label="Onboarding incomplete" value={data?.onboardingIncomplete} />
          <Kpi icon={FileText} label="Welcome emails sent" value={data?.welcomeEmailsSent} />
          <Kpi icon={FileText} label="Account emails sent" value={data?.emailsSent} />
          <Kpi icon={Flag} label="Email failures" value={data?.emailsFailed} />
        </div>
      )}
    </div>
  );
}
